import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { SmsAdvCampaignStatus, SmsAdvCampaignStatusHTMLSpanDraw } from '@models/smsadv/smsadv';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { SmsAdvCampaignsService } from '@services/sms-adv-campaigns.service';
import { ActionSmsAdv, SmsAdvService } from '@services/smsadv.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { formateDateTimeWithSeconds } from '@utils/utils';
import { GridReadyEvent } from 'ag-grid-community';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-sms-adv-list-scheduled',
  templateUrl: './sms-adv-list-scheduled.component.html',
  styleUrls: ['./sms-adv-list-scheduled.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class SmsAdvListScheduledComponent implements OnInit {
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('modalConfirmDelete') modalConfirmDelete;
  modalConfirmDeleteModal: NgbModalRef;

  tableColumnsDefs;
  tableGridOptions;
  tableLoading: boolean;
  tableRowData = null;
  paginationPageSize = 10;
  isLoaderActive = false;

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  gridAnimationState = 'hide';

  constructor(
    private translateService: TranslateService,
    private atlasToast: AtlasToastService,
    private modalService: NgbModal,
    private router: Router,
    private loaderService: LoaderService,
    private smsAdvService: SmsAdvService,
    private smsAdvCampaignService: SmsAdvCampaignsService
  ) {}

  ngOnInit(): void {
    this.defineTable();
    this.gridOptionDefinition();
  }

  defineTable() {
    this.tableColumnsDefs = [
      {
        headerNameTranslate: 'email_campaign_info',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              return this.smsAdvCampaignHtml(params.data);
            }
            return '';
          }
        },
        sortable: false,
        cellStyle: { 'line-height': '25px' },
        width: 200
      },
      {
        headerNameTranslate: 'email_campaign_status',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            return params && params.data ? this.getHtmlRenderStatus('mail_send_status_3', SmsAdvCampaignStatusHTMLSpanDraw.SCHEDULED) : '';
          }
        },
        suppressCellFocus: true,
        sortable: false,
        width: 120
      },
      {
        headerNameTranslate: 'smsadv_list_table_created_on',
        cellRenderer: 'htmlRenderer',
        sortable: false,
        sort: 'desc',
        width: 110,
        cellRendererParams: {
          content: params => {
            if (params && params.data && params.data.createDate) {
              const dateTime = formateDateTimeWithSeconds(params.data.createDate).split(' ');
              const date = dateTime[0];
              const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
              return '<div class="dateTime">' + '</span><span class="mb-neg-15">' + date + '</span> <span>' + time + '</span></div>';
            }
            return '';
          }
        }
      },
      {
        headerNameTranslate: 'sms_detail_sendtime',
        cellRenderer: 'htmlRenderer',
        sortable: false,
        sort: 'desc',
        width: 110,
        cellRendererParams: {
          content: params => {
            if (params && params.data && params.data.scheduledDate) {
              const dateTime = formateDateTimeWithSeconds(params.data.scheduledDate).split(' ');
              const date = dateTime[0];
              const time = dateTime[2] ? dateTime[1] + ' ' + dateTime[2] : dateTime[1];
              return '<div class="dateTime"><span class="mb-neg-15">' + date + '</span> <span>' + time + '</span></div>';
            }
            return '';
          }
        }
      },
      {
        headerNameTranslate: 'email_campaign_recipients',
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              return '<div class="campaign-email-recipients">' + this.getRecipient(params.data) + '</div>';
            }
            return '';
          }
        },
        width: 100,
        suppressCellFocus: true,
        sortable: false
      },
      {
        headerNameTranslate: 'common_actions',
        suppressCellFocus: true,
        cellRenderer: 'buttonGroupRenderer',
        cellRendererParams: {
          items: [
            {
              onClick: e => this.copyAction(e.idSmsAdvCampaign),
              label: 'common_copy'
            },
            {
              onClick: e => this.openConfirmDeleteModal(e.idSmsAdvCampaign),
              label: 'common_delete'
            }
          ]
        },

        cellClass: ['button-group', 'text-end'],
        maxWidth: 82,
        field: '__actions'
      }
    ];
  }

  gridOptionDefinition() {
    const that = this;
    this.tableGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.gridAnimationState = 'show';
      },
      rowHeight: 110,
      pagination: true,
      // resizable: false,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadList.bind(that),
      noRowsOverlayOptions: {
        message: ''
      }
    });
    this.reloadList();
  }

  reloadList(startRow?, endRow?, sort?) {
    if (!this.isLoaderActive) {
      this.loaderService.show();
      this.isLoaderActive = true;
    }
    return this.smsAdvCampaignService
      .getSmsAdvCampaignsList(1 + startRow / (endRow - startRow), endRow - startRow, null, 'd', [
        SmsAdvCampaignStatus.SCHEDULED,
        SmsAdvCampaignStatus.PENDING
      ])
      .pipe(
        finalize(() => {
          this.loaderService.hide();
          this.isLoaderActive = false;
        })
      );
  }

  formatDate(date) {
    return;
  }

  smsAdvCampaignHtml(campaign) {
    const label = this.translateService.instant('sender_sender');

    return (
      '<div class="campaign-sms-adv-title">' +
      campaign.title +
      '</div>' +
      '<div class="campaign-sms-adv-message">' +
      campaign.message +
      '</div>' +
      '<div>' +
      '<span class="campaign-sms-adv-message">' +
      label +
      '</span>' +
      ': ' +
      '<span>' +
      campaign.sender.valueSender +
      '</span>' +
      '</div>'
    );
  }

  private getRecipient(data) {
    return data.limitRecipients > 0 ? data.limitRecipients : data.countRecipients;
  }

  private getHtmlRenderStatus(label, spanHTML) {
    const labelStatus = this.getTranslateLabels(label);
    return spanHTML + labelStatus + '</span>';
  }

  private getTranslateLabels(value: string) {
    if (value) {
      let labels;
      this.translateService.get(value).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  private openConfirmDeleteModal(smsAdvCampaignId) {
    this.modalConfirmDeleteModal = this.modalService.open(this.modalConfirmDelete);

    this.modalConfirmDeleteModal.result.then(
      result => {
        this.deleteSmsAdvCampaign(smsAdvCampaignId);
      },
      reason => {}
    );
  }

  deleteSmsAdvCampaign(idSmsAdvCampaign: number) {
    this.smsAdvService.deleteSmsAdvCampaign(idSmsAdvCampaign).subscribe(() => {
      this.grid.refresh();
      this.atlasToast.success('common_success', null);
    });
  }

  closeDelete() {
    this.modalConfirmDeleteModal.close();
  }

  private copyAction(id: any) {
    if (id && id > 0) {
      this.callApiCopySmsAdvCampaign(id);
    } else if (id <= 0) {
      this.atlasToast.error('common_error', null);
    }
  }

  private callApiCopySmsAdvCampaign(id: any) {
    this.smsAdvService.copySmsAdvCampaign(id).subscribe(
      response => this.handlerActionResponse(response, ActionSmsAdv.ACTION_COPY),
      error => this.atlasToast.error('common_error', null)
    );
  }

  private handlerActionResponse(response, action: ActionSmsAdv) {
    const data = response;
    const param = { action, data };
    this.smsAdvService.setActionSmsAdv(param);
    this.router.navigate(['/smsadv/create']);
    this.atlasToast.success('common_success', null);
  }
}
